export type FounderAuditLogEntry = {
  id: string;
  action: string;
  entity_type: string | null;
  created_at: string;
};

export type FounderSecurityAlertEntry = {
  id: string;
  event_type: string;
  severity: string | null;
  message: string | null;
  created_at: string;
};

export type FounderActivityItem = {
  id: string;
  label: string;
  occurredAt: string;
  kind: "audit" | "security";
};

const AUDIT_ACTION_LABELS: Record<string, string> = {
  "publisher.registered": "A new publisher joined OpenAppCart",
  "application.submission.created": "An app was submitted for review",
  "application.publication.completed_after_payment": "An app went live after payment",
  "application.publication.blocked_after_payment": "An app publication was blocked after payment",
  "application.publisher.unpublished": "A publisher removed an app from the marketplace",
  "application.metadata.updated": "An app listing was updated",
  "application.version.uploaded": "A new app version was uploaded",
  "application.version.activated": "A new app version went live",
  "application.version.update_completed": "An app update was completed",
  "application.version.security_blocked": "An app update was blocked for security reasons",
  "application.security_scan.completed": "A security scan finished",
  "subscription.created": "A new membership subscription was started",
  "payment.succeeded": "A payment was received",
  "payment.verification.amount_mismatch": "A payment verification issue was detected",
  "renewal.transaction.created": "A membership renewal was started",
  "renewal.payment.verified": "A membership renewal payment was confirmed",
  "renewal.applied": "A membership renewal was applied",
  "publisher.agreement.accepted": "A publisher accepted the agreement",
  "founder.platform.publication_mode.updated": "Publication settings were changed",
  "founder.category.created": "A new marketplace category was added",
  "founder.membership_plan.created": "A new membership plan was added",
  "founder.membership_plan.updated": "A membership plan price or renewal rule was updated",
  "founder.legal_document.created": "A legal document was updated",
  "founder.publisher.active": "A publisher account was restored",
  "founder.publisher.suspended": "A publisher account was suspended",
  "founder.application.approve": "An app was approved",
  "founder.application.request_revision": "An app was sent back for changes",
  "founder.application.reject": "An app submission was rejected",
  "founder.application.suspend": "An app was suspended",
  "founder.application.restore": "An app was restored",
  "founder.application.delist": "An app was removed from the marketplace",
};

function humanizeAction(action: string): string {
  return action
    .replaceAll(".", " ")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatFounderAuditActivity(log: FounderAuditLogEntry): string {
  return AUDIT_ACTION_LABELS[log.action] ?? humanizeAction(log.action);
}

export function formatFounderSecurityActivity(alert: FounderSecurityAlertEntry): string {
  if (alert.message?.trim()) {
    return alert.message.trim();
  }

  const eventLabel = alert.event_type
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return `Security alert: ${eventLabel}`;
}

export function mergeFounderRecentActivity(
  auditLogs: FounderAuditLogEntry[],
  securityAlerts: FounderSecurityAlertEntry[],
  limit = 8,
): FounderActivityItem[] {
  const auditItems: FounderActivityItem[] = auditLogs.map((log) => ({
    id: `audit-${log.id}`,
    label: formatFounderAuditActivity(log),
    occurredAt: log.created_at,
    kind: "audit",
  }));

  const securityItems: FounderActivityItem[] = securityAlerts.map((alert) => ({
    id: `security-${alert.id}`,
    label: formatFounderSecurityActivity(alert),
    occurredAt: alert.created_at,
    kind: "security",
  }));

  return [...auditItems, ...securityItems]
    .sort(
      (left, right) =>
        new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
    )
    .slice(0, limit);
}
